import i18n from "i18next";
import { SingleCustomerOfferViewApi } from "src/api/generated/erp/sales/customerOrderOffer/api/singleCustomerOfferViewApi.ts";
import { AsyncFetchRender } from "src/components/common/async/AsyncFetchRender.tsx";
import { AavoMosaic } from "src/components/common/mosaic/AavoMosaic.tsx";
import { CustomerOfferInfoView } from "src/components/views/erp/sales/customerOrderOffer/CustomerOfferInfoView.tsx";
import { Permissions } from "src/api/generated/iam/permissions.ts";
import { useHasPermission } from "src/components/views/erp/common/userPermissions.ts";
import { useTenantCustomizations } from "src/tenantCustomizations/TenantCustomizationsContext.ts";
import { useTasksEnabled } from "src/components/views/tasks/useTasksEnabled.ts";
import { useRefreshRef } from "src/utils/useRefreshRef.ts";
import { CustomerOrderOfferLinesDataGrid } from "src/components/views/erp/sales/customerOrderOffer/CustomerOrderOfferLinesDataGrid.tsx";
import { SingleSourceTasksView } from "src/components/views/tasks/pages/SingleSourceTasksView.tsx";
import { CustomerOrderChangesDataGrid } from "src/components/views/erp/sales/customerOrderChange/CustomerOrderChangesDataGrid.tsx";
import { CustomerOfferBillingPlanDataGrid } from "src/components/views/erp/sales/billingPlan/customerOffer/CustomerOfferBillingPlanDataGrid.tsx";
import { CustomerOfferSalesCommissionView } from "src/components/views/erp/sales/salesCommissions/orderAndOfferViews/offer/CustomerOfferSalesCommissionView.tsx";
import { DocumentsOfSingleObjectView } from "src/components/views/documents/objectDocuments/DocumentsOfSingleObjectView.tsx";
import { PartCostOverridesDataGrid } from "src/components/views/erp/sales/partCostOverride/PartCostOverridesDataGrid.tsx";
import { CustomerOrderOfferProfitMarginsDataGrid } from "src/components/views/erp/sales/customerOrderOffer/CustomerOrderOfferProfitMarginsDataGrid.tsx";
import { canEditCustomerOrderOffer } from "src/components/views/erp/sales/customerOrderOffer/customerOrderOfferStateGuards.ts";
import { getCustomerOfferOrderChangesMode } from "src/components/views/erp/sales/customerOrderOffer/customerOfferOrderChanges.ts";
import { AavoMosaicTabItem } from "src/components/common/mosaic/types.ts";
import { CustomerOrderOfferView } from "src/api/generated/erp/db/types/tables/customerOrderOfferView.ts";

export interface SingleCustomerOfferViewProps {
	customerOrderOfferId: number;
}

export const SingleCustomerOfferView = ({ customerOrderOfferId }: SingleCustomerOfferViewProps) => {
	const { tenantConfig } = useTenantCustomizations();
	const tasksEnabled = useTasksEnabled();
	const hasPermission = useHasPermission();
	const editable = hasPermission(Permissions.Sales.CustomerOffers.MANAGE);
	const customerOrderOfferRefreshRef = useRefreshRef();
	const offerLinesRefreshRef = useRefreshRef();
	const offerBillingPlanRefreshRef = useRefreshRef();

	return (
		<AsyncFetchRender
			fetch={() =>
				SingleCustomerOfferViewApi.getSingleCustomerOfferViewData({
					customerOrderOfferId: customerOrderOfferId,
				})
			}
			refreshRef={customerOrderOfferRefreshRef}
			content={({ offer, summary, attributes }, reload) => (
				<AavoMosaic
					viewId={"83A55AB84989C8D1"}
					layout={{
						type: "row",
						splitPercentage: 30,
						first: {
							type: "panel",
							content: (
								<CustomerOfferInfoView
									customerOrderOffer={offer}
									summary={summary}
									attributes={attributes}
									refreshData={async () => {
										await reload();
									}}
									offerLinesRefreshRef={offerLinesRefreshRef}
									editable={editable}
								/>
							),
						},
						second: {
							type: "tabs",
							items: [
								{
									type: "panel",
									title: i18n.t("offer_lines"),
									content: (
										<CustomerOrderOfferLinesDataGrid
											key={offer.customerOrderOfferId}
											customerOrderOfferId={offer.customerOrderOfferId}
											parentLineId={null}
											hideSummary
											refreshRef={offerLinesRefreshRef}
											editable={editable}
										/>
									),
								},
								tenantConfig.documentsEnabled && {
									type: "panel",
									title: i18n.t("documents"),
									content: (
										<DocumentsOfSingleObjectView
											key={offer.customerOrderOfferId}
											objectRef={{
												objectType: "CUSTOMER_ORDER_OFFER",
												objectId: offer.customerOrderOfferId,
											}}
											editable={editable}
											hidePreview
										/>
									),
								},
								tasksEnabled && {
									type: "panel",
									title: i18n.t("tasks"),
									content: (
										<SingleSourceTasksView
											key={offer.customerOrderOfferId}
											taskSourceRef={{
												sourceType: "CUSTOMER_ORDER_OFFER",
												sourceId: offer.customerOrderOfferId,
											}}
										/>
									),
								},
								getOrderChangesTab(offer),
								{
									type: "panel",
									title: i18n.t("billing_plan"),
									hidden: !offer.billingPlanEnabled,
									content: (
										<CustomerOfferBillingPlanDataGrid
											key={offer.customerOrderOfferId}
											customerOfferId={offer.customerOrderOfferId}
											customerOrderOffersRefreshRef={customerOrderOfferRefreshRef}
											refreshRef={offerBillingPlanRefreshRef}
										/>
									),
								},
								offer.salesCommissionId != null &&
									(hasPermission(Permissions.Sales.CustomerOfferCommissions.READ) ||
										hasPermission(Permissions.Sales.CustomerOfferCommissions.MANAGE)) && {
										type: "panel",
										title: i18n.t("commissions"),
										content: (
											<CustomerOfferSalesCommissionView
												key={offer.salesCommissionId}
												salesCommissionId={offer.salesCommissionId}
												customerOffer={offer}
											/>
										),
									},
								offer.customerPriceGroupPricingMethod === "PROFIT_MARGIN" && {
									type: "panel",
									title: i18n.t("profit_margins"),
									content: (
										<CustomerOrderOfferProfitMarginsDataGrid
											key={offer.customerOrderOfferId}
											customerOrderOfferId={offer.customerOrderOfferId}
											onMarginsSaved={async () => {
												await Promise.all([
													customerOrderOfferRefreshRef.refresh(),
													offerLinesRefreshRef.refresh(),
												]);
											}}
											editable={
												editable && canEditCustomerOrderOffer(offer.customerOrderOfferState)
											}
										/>
									),
								},
								tenantConfig.erp.partCostOverridesEnabled &&
									hasPermission(Permissions.Sales.CustomerOffers.READ_COSTS) && {
										type: "panel",
										title: i18n.t("part_cost_overrides"),
										content: (
											<PartCostOverridesDataGrid
												key={offer.customerOrderOfferId}
												owner={{
													type: "customerOffer",
													customerOrderOfferId: offer.customerOrderOfferId,
												}}
												onCostsSaved={async () => {
													await Promise.all([
														customerOrderOfferRefreshRef.refresh(),
														offerLinesRefreshRef.refresh(),
													]);
												}}
												editable={
													editable && canEditCustomerOrderOffer(offer.customerOrderOfferState)
												}
											/>
										),
									},
							],
						},
					}}
				/>
			)}
		/>
	);

	function getOrderChangesTab(offer: CustomerOrderOfferView): AavoMosaicTabItem | false {
		const mode = getCustomerOfferOrderChangesMode(offer);
		return (
			mode != null && {
				type: "panel",
				title: i18n.t("order_changes"),
				content: (
					<CustomerOrderChangesDataGrid
						key={offer.customerOrderOfferId}
						objectType="CUSTOMER_OFFER"
						objectId={offer.customerOrderOfferId}
						readOnly={mode === "readOnly" || !editable}
					/>
				),
			}
		);
	}
};
